import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CheckCircle2, Repeat, Loader2, Sparkles, Hourglass } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { useSort, SortHead } from "../components/SortHead";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DatePickerHR } from "../components/DatePickerHR";
import api, { fmtNum, fmtDate } from "../lib/api";

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthOptions = () => {
  const opts = [];
  const d = new Date();
  for (let i = 12; i >= -12; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
    opts.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return opts;
};
const MONTHS_HR = ["siječanj", "veljača", "ožujak", "travanj", "svibanj", "lipanj", "srpanj", "kolovoz", "rujan", "listopad", "studeni", "prosinac"];
const label = (m) => `${MONTHS_HR[parseInt(m.slice(5)) - 1]} ${m.slice(0, 4)}.`;

const emptyForm = { name: "", bank_id: "", type: "placanje", currency: "EUR", amount: "", category: "zakup", first_due: "", end_month: "", active: true };

const NO_END = "__none__";

const endMonthOptions = () => {
  const opts = [];
  const d = new Date();
  for (let i = -12; i <= 48; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
    opts.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return opts;
};

const firstDueFrom = (rec, fallbackMonth) => {
  const m = rec.start_month || fallbackMonth;
  const [y, mo] = m.split("-").map(Number);
  const last = new Date(y, mo, 0).getDate();
  return `${m}-${String(Math.min(rec.day_of_month || 1, last)).padStart(2, "0")}`;
};

export default function RecurringPage() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [bookingAll, setBookingAll] = useState(false);
  const [smart, setSmart] = useState([]);
  const [ending, setEnding] = useState([]);

  const loadEnding = () => api.get("/recurring/ending").then((r) => setEnding(r.data.items)).catch(() => {});

  const load = useCallback(() => {
    api.get("/recurring/status", { params: { month } }).then((r) => setData(r.data));
    loadEnding();
  }, [month]);

  const loadSmart = () => api.get("/smart/suggestions").then((r) => setSmart(r.data.suggestions)).catch(() => {});

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    loadSmart();
  }, []);
  useEffect(() => { load(); }, [load]);

  const acceptSmart = async (s, mode) => {
    try {
      await api.post("/smart/accept", {
        counterparty: s.counterparty, type: s.type, currency: s.currency, amount: s.amount,
        day_of_month: s.day_of_month, category: s.category, bank_id: s.bank_id, mode, next_date: s.next_date,
      });
      toast.success(mode === "recurring" ? `"${s.counterparty}" dodano u fiksne stavke` : `"${s.counterparty}" planirano za ${fmtDate(s.next_date)}`);
      loadSmart();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška");
      loadSmart();
    }
  };

  const dismissSmart = async (s) => {
    await api.post("/smart/dismiss", { counterparty: s.counterparty, type: s.type, currency: s.currency });
    toast.success("Prijedlog odbačen — više se neće predlagati");
    loadSmart();
  };

  const openDialog = (rec) => {
    setEditing(rec || null);
    setForm(rec
      ? { ...rec, amount: String(rec.amount), first_due: firstDueFrom(rec, month), end_month: rec.end_month || "" }
      : { ...emptyForm, bank_id: banks[0]?.id || "" });
    setDialogOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_due) { toast.error("Odaberite datum prvog dospijeća"); return; }
    const startMonth = form.first_due.slice(0, 7);
    if (form.end_month && form.end_month < startMonth) { toast.error("Zadnji mjesec ne može biti prije prvog dospijeća"); return; }
    setBusy(true);
    const payload = {
      ...form,
      amount: parseFloat(String(form.amount).replace(",", ".")),
      day_of_month: parseInt(form.first_due.slice(8), 10),
      start_month: startMonth,
      end_month: form.end_month || "",
    };
    delete payload.first_due;
    try {
      if (editing) await api.put(`/recurring/${editing.id}`, payload);
      else await api.post("/recurring", payload);
      toast.success(editing ? "Fiksni trošak ažuriran" : "Fiksni trošak dodan");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spremanju");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Obrisati fiksni trošak? (već prebačene transakcije ostaju)")) return;
    await api.delete(`/recurring/${id}`);
    toast.success("Obrisano");
    load();
  };

  const toggleActive = async (rec) => {
    await api.put(`/recurring/${rec.id}`, { active: !rec.active });
    load();
  };

  const book = async (id) => {
    try {
      await api.post(`/recurring/${id}/book`, { month });
      toast.success(`Prebačeno u planirane transakcije za ${label(month)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri prebacivanju");
    }
  };

  const bookAll = async () => {
    setBookingAll(true);
    try {
      const r = await api.post("/recurring/book-all", { month });
      toast.success(`Prebačeno ${r.data.booked} fiksnih stavki u planirane transakcije`);
      load();
    } catch {
      toast.error("Greška pri prebacivanju");
    } finally {
      setBookingAll(false);
    }
  };

  const bankName = (id) => banks.find((b) => b.id === id)?.short || "?";
  const items = data?.items || [];
  const endingById = Object.fromEntries(ending.map((e) => [e.id, e]));
  const rAccessors = {
    name: (r) => r.name.toLowerCase(),
    bank: (r) => bankName(r.bank_id),
    category: (r) => r.category,
    due: (r) => r.due_date,
    amount: (r) => (r.currency === "BAM" ? r.amount / 1.9558 : r.amount),
  };
  const { sorted: sortedItems, sort, toggle } = useSort(items, rAccessors);

  return (
    <div className="space-y-6 fade-up" data-testid="recurring-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Svaki mjesec</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Fiksni troškovi/prihodi</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="recurring-month-select" className="w-44 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions().map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          {data?.pending > 0 && (
            <Button data-testid="book-all-btn" onClick={bookAll} disabled={bookingAll} title={`Sve važeće stavke koje još nisu prebačene postat će planirane transakcije za ${label(month)} — ulaze u projekcije salda i čekaju uparivanje s izvodom`} variant="outline" className="rounded-sm border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/5">
              {bookingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Repeat className="w-4 h-4 mr-2" />}
              Prebaci sve ({data.pending})
            </Button>
          )}
          <Button data-testid="add-recurring-btn" onClick={() => openDialog(null)} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Novi fiksni trošak
          </Button>
        </div>
      </div>

      <div data-testid="recurring-explain" className="bg-white border border-[#E2E8F0] rounded-sm px-4 py-3 text-sm text-[#0F172A] space-y-1">
        <p>
          <b>Fiksni troškovi/prihodi</b> su stavke koje se ponavljaju svaki mjesec — zakup, plaće, leasing, pretplate, ali i redoviti prihodi (npr. dugoročni najam).
          Ovdje ih definirate jednom, s danom dospijeća i razdobljem važenja.
        </p>
        <p className="text-[#475569]">
          Gumbom <b>"Prebaci"</b> (ili "Prebaci sve") stavka se za odabrani mjesec <b>prebacuje u planiranu transakciju</b> — vidi se na Transakcijama sa statusom "Planirano",
          ulazi u projekcije salda (Dnevni saldo, Kalendar, Pregled) i čeka uparivanje s izvodom. Kad uplata/isplata stigne na izvod, plan se automatski označi kao ostvaren.
          Svaka stavka se za isti mjesec može prebaciti samo jednom.
        </p>
      </div>

      {smart.length > 0 && (
        <div data-testid="smart-suggestions-section" className="bg-white border border-[#D97706]/40 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-[#D97706]/10 border-b border-[#D97706]/30 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D97706]" />
            <p className="text-sm font-semibold text-[#0F172A]">Pametni prijedlozi — troškovi/prihodi koji se ponavljaju ({smart.length})</p>
            <p className="text-xs text-[#475569] hidden sm:block">Prepoznati iz povijesti (≥3 mjeseca, sličan iznos), a nisu pokriveni fiksnim stavkama ni otvorenim planovima.</p>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {smart.map((s, i) => (
              <div key={`${s.counterparty}-${s.type}-${s.currency}`} data-testid={`smart-suggestion-${i}`} className="px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="min-w-[220px] flex-1">
                  <p className="text-sm font-semibold text-[#0F172A]">{s.counterparty}</p>
                  <p className="text-xs text-[#475569]">
                    {s.type === "placanje" ? "odljev" : "priljev"} · {s.category} · viđeno u {s.months_seen} mj. ({s.occurrences}×) · obično oko {s.day_of_month}. u mjesecu · zadnje {fmtDate(s.last_date)}
                  </p>
                </div>
                <p className={`text-sm font-bold tabular-nums ${s.type === "naplata" ? "text-[#2F7D4F]" : "text-[#DC2626]"}`}>~{fmtNum(s.amount)} {s.currency}</p>
                <div className="flex items-center gap-2">
                  <Button data-testid={`smart-add-recurring-${i}`} size="sm" onClick={() => acceptSmart(s, "recurring")} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm h-8 text-xs">
                    <Repeat className="w-3.5 h-3.5 mr-1.5" /> Dodaj u fiksne
                  </Button>
                  <Button data-testid={`smart-add-plan-${i}`} size="sm" variant="outline" onClick={() => acceptSmart(s, "plan")} className="rounded-sm h-8 text-xs border-[#CBD5E1]">
                    Planiraj {fmtDate(s.next_date)}
                  </Button>
                  <Button data-testid={`smart-dismiss-${i}`} size="sm" variant="ghost" onClick={() => dismissSmart(s)} className="rounded-sm h-8 text-xs text-[#64748B] hover:text-[#DC2626]">
                    Odbaci
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {ending.length > 0 && (
        <div data-testid="recurring-ending-banner" className="bg-white border border-[#DC2626]/40 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-[#DC2626]/10 border-b border-[#DC2626]/30 flex items-center gap-2">
            <Hourglass className="w-4 h-4 text-[#DC2626]" />
            <p className="text-sm font-semibold text-[#0F172A]">Rate pri kraju ({ending.length}) — provjerite treba li obnoviti ili ukinuti</p>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {ending.map((e, i) => (
              <div key={e.id} data-testid={`recurring-ending-item-${i}`} className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
                <span className="font-medium text-[#0F172A] min-w-[180px] flex-1">{e.name}</span>
                <span className="tabular-nums text-[#475569]">{e.type === "naplata" ? "+" : "−"}{fmtNum(e.amount, e.currency === "EUR" ? "€" : "KM")} · do {e.end_month.slice(5)}/{e.end_month.slice(0, 4)}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${e.months_left <= 0 ? "bg-[#DC2626]/15 text-[#DC2626]" : "bg-[#D97706]/15 text-[#92400E]"}`}>{e.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
              <SortHead label="Naziv" sortKey="name" sort={sort} onToggle={toggle} />
              <SortHead label="Banka" sortKey="bank" sort={sort} onToggle={toggle} />
              <SortHead label="Kategorija" sortKey="category" sort={sort} onToggle={toggle} />
              <SortHead label="Dospijeće" sortKey="due" sort={sort} onToggle={toggle} />
              <SortHead label="Iznos" sortKey="amount" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <TableHead>Status ({label(month)})</TableHead>
              <TableHead>Aktivan</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="recurring-table-body">
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-[#64748B] py-10">Nema fiksnih troškova. Dodajte npr. zakup, pretplate, režije...</TableCell></TableRow>
            )}
            {sortedItems.map((r) => (
              <TableRow key={r.id} className={`hover:bg-[#F8FAFC] transition-colors duration-200 ${r.active ? "" : "opacity-50"}`} data-testid={`recurring-row-${r.id}`}>
                <TableCell className="text-sm font-medium">{r.name}</TableCell>
                <TableCell className="text-sm">{bankName(r.bank_id)}</TableCell>
                <TableCell className="text-sm text-[#475569]">{r.category}</TableCell>
                <TableCell className="text-sm tabular-nums">
                  {fmtDate(r.due_date)}
                  {r.validity_label && <span className="block text-[11px] text-[#64748B]" data-testid={`validity-${r.id}`}>{r.validity_label}</span>}
                  {r.installments_booked != null && (
                    <span className="block text-[11px] text-[#0D9488] font-medium" data-testid={`rates-${r.id}`}>
                      {r.installments_total != null
                        ? `rata ${r.installments_booked}/${r.installments_total} · ostaje ${r.installments_left}`
                        : `prebačeno ${r.installments_booked} rata`}
                    </span>
                  )}
                  {endingById[r.id] && (
                    <span data-testid={`ending-badge-${r.id}`} className={`inline-flex items-center gap-1 mt-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-sm ${endingById[r.id].months_left <= 0 ? "bg-[#DC2626]/15 text-[#DC2626]" : "bg-[#D97706]/15 text-[#92400E]"}`}>
                      <Hourglass className="w-3 h-3" /> {endingById[r.id].label}
                    </span>
                  )}
                </TableCell>
                <TableCell className={`text-right tabular-nums font-semibold text-sm ${r.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                  {r.type === "naplata" ? "+" : "−"}{fmtNum(r.amount, r.currency === "EUR" ? "€" : "KM")}
                </TableCell>
                <TableCell>
                  {r.booked ? (
                    <Badge className="bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/10 rounded-sm text-xs gap-1" data-testid={`status-booked-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Prebačeno
                    </Badge>
                  ) : r.valid_for_month === false ? (
                    <Badge className="bg-[#64748B]/10 text-[#64748B] hover:bg-[#64748B]/10 rounded-sm text-xs" data-testid={`status-invalid-${r.id}`}>Ne važi za ovaj mjesec</Badge>
                  ) : r.active ? (
                    <Badge className="bg-[#D97706]/10 text-[#D97706] hover:bg-[#D97706]/10 rounded-sm text-xs" data-testid={`status-pending-${r.id}`}>Na čekanju</Badge>
                  ) : (
                    <span className="text-xs text-[#64748B]">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Switch data-testid={`toggle-active-${r.id}`} checked={r.active} onCheckedChange={() => toggleActive(r)} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end items-center">
                    {!r.booked && r.active && r.valid_for_month !== false && (
                      <Button data-testid={`book-btn-${r.id}`} size="sm" onClick={() => book(r.id)} title={`Prebacuje stavku u PLANIRANU transakciju za ${label(month)} — vidi se na Transakcijama, ulazi u projekcije salda i čeka uparivanje s izvodom`} className="h-7 bg-[#0D9488] hover:bg-[#0F766E] rounded-sm text-xs">Prebaci</Button>
                    )}
                    <button data-testid={`edit-recurring-${r.id}`} onClick={() => openDialog(r)} className="p-1.5 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-recurring-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 text-[#64748B] hover:text-[#DC2626] transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Uredi fiksni trošak" : "Novi fiksni trošak"}</DialogTitle>
            <DialogDescription className="sr-only">Predložak mjesečnog troška</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Naziv</Label>
              <Input data-testid="rec-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="npr. zakup ureda, Figma pretplata..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Banka</Label>
                <Select value={form.bank_id} onValueChange={(v) => set("bank_id", v)}>
                  <SelectTrigger data-testid="rec-bank-select"><SelectValue placeholder="Odaberi" /></SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.short || b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tip</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="rec-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placanje">Plaćanje (odljev)</SelectItem>
                    <SelectItem value="naplata">Naplata (priljev)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Iznos</Label>
                <Input data-testid="rec-amount-input" type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-1.5">
                <Label>Valuta</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="rec-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="BAM">BAM</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Prvo dospijeće</Label>
                <DatePickerHR data-testid="rec-first-due" value={form.first_due} onChange={(v) => set("first_due", v)} className="w-full" />
                <p className="text-[11px] text-[#64748B]">Vrijedi od tog mjeseca, dospijeva svaki mjesec na taj dan</p>
              </div>
              <div className="space-y-1.5">
                <Label>Vrijedi do (opcionalno)</Label>
                <Select value={form.end_month || NO_END} onValueChange={(v) => set("end_month", v === NO_END ? "" : v)}>
                  <SelectTrigger data-testid="rec-end-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value={NO_END}>Bez ograničenja</SelectItem>
                    {endMonthOptions().map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#64748B]">Npr. zadnja rata leasinga</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Kategorija</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="rec-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="rec-save-btn" type="submit" disabled={busy || !form.bank_id} className="w-full bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              {busy ? "Spremanje..." : "Spremi"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
